import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/api/client";

const AuthContext = createContext(null);

// Where to send the user after logout (the skiverse portal / identity source).
const LOGOUT_REDIRECT =
  process.env.REACT_APP_LOGOUT_URL || "https://skiverse.skicorp.in/app-dashboard";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("t360_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);

  // Strip ?token=... from the URL without reloading the SPA.
  const clearTokenParam = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("token");
    const qs = params.toString();
    const clean = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", clean);
  };

  const ssoLogin = async (ssoToken) => {
    const r = await api.post("/auth/sso", { token: ssoToken });
    if (!r.data?.success) throw new Error(r.data?.message || "SSO failed");
    const { access_token, refresh_token, user: u } = r.data.data;
    localStorage.setItem("t360_access_token", access_token);
    localStorage.setItem("t360_refresh_token", refresh_token);
    localStorage.setItem("t360_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  useEffect(() => {
    // 1) SSO token in URL (e.g. /?token=DCPL-<hash>) takes priority.
    const ssoToken = new URLSearchParams(window.location.search).get("token");
    if (ssoToken) {
      ssoLogin(ssoToken)
        .catch((e) => console.warn("[sso]", e?.response?.data?.message || e.message))
        .finally(() => { clearTokenParam(); setLoading(false); });
      return;
    }

    // 2) Otherwise resume an existing session.
    const token = localStorage.getItem("t360_access_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      const u = r.data?.data;
      if (u?.id) {
        setUser(u);
        localStorage.setItem("t360_user", JSON.stringify(u));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (!r.data?.success) throw new Error(r.data?.message || "Login failed");
    const { access_token, refresh_token, user: u } = r.data.data;
    localStorage.setItem("t360_access_token", access_token);
    localStorage.setItem("t360_refresh_token", refresh_token);
    localStorage.setItem("t360_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("t360_access_token");
    localStorage.removeItem("t360_refresh_token");
    localStorage.removeItem("t360_user");
    setUser(null);
    // Return to the skiverse portal (identity source) after logout.
    window.location.href = LOGOUT_REDIRECT;
  };

  const hasPerm = (...perms) => {
    if (!user) return false;
    if (user.role_code === "super_admin") return true;
    const set = new Set(user.permissions || []);
    if (set.has("*")) return true;
    return perms.some((p) => set.has(p));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
